import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { QuickAnswerCard, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { TUTOR_GUIDE_AEO } from "../data/aeoContent";
import {
    UserCheck, BookOpen, ListChecks, Search, ClipboardCheck, Users2,
    CalendarPlus, Bell, GraduationCap, Presentation, ShieldAlert, CheckCircle2,
    HelpCircle, IndianRupee, PhoneCall, MessageCircle, ChevronDown, ChevronRight,
    Home as HomeIcon, ArrowRight, Printer, Download, Share2, Copy, Timer,
    Wallet, CalendarDays, LayoutDashboard, Star, Sparkles, ExternalLink,
    FileText, Clock, MapPin, PenLine,
} from "lucide-react";

/* -----------------------------------------------------------------------------
 *  Static content — kept in-file so the page renders instantly on first paint
 *  (no lazy fetch, no DB call — this is a documentation page, content is
 *  fixed and search-engine friendly).
 * ---------------------------------------------------------------------------*/

// Single smart-redirect landing page — routes users to Google Play (Android)
// or Apple App Store (iOS) based on their device. Same URL for both buttons
// so no OS mismatch, no dead links.
const APP_DOWNLOAD_URL = "https://www.bookmytutor.com/download";
const SUPPORT_PHONES = ["+91 7000530316", "+91 99933 05574"];
const WHATSAPP_NUMBER = "917000530316"; // V68 — Global WhatsApp destination.
const CONTACT_MESSAGE = encodeURIComponent(
    "Hi Anand Tutorials — I have a question about the Tutor Guide (Book My Tutor)."
);

const STEPS = [
    {
        n: 1, id: "step-profile", icon: UserCheck, title: "Complete Your Profile",
        summary: "Add every detail — a complete profile dramatically improves your selection rate.",
        body: [
            { type: "list", title: "Sections to complete", items: [
                "Aadhaar Verification (Front + Back)",
                "Courses & Subjects you teach",
                "Grades / Classes you handle",
                "Boards / Curriculum (CBSE, ICSE, IB, IGCSE, State Boards, etc.)",
                "Home Location (city, area, PIN code, travel radius)",
                "Profile details (qualification, experience, availability, gender)",
            ]},
            { type: "tip", tone: "amber", text: "A complete profile increases your chances of getting selected. Institutes always prefer tutors whose credentials, subjects and area are clearly visible." },
        ],
    },
    {
        n: 2, id: "step-registered", icon: BookOpen, title: "Registration Completed",
        summary: "You'll land on the Book My Tutor home page — your tutor command center.",
        body: [
            { type: "text", text: "After successful registration you will reach the Book My Tutor Home Page inside the app. This is your dashboard for everything — available assignments, active tuitions, attendance, and payments." },
            { type: "phone-mockup", src: "/tutor-guide/bmt-home.svg", caption: "Book My Tutor · Home" },
        ],
    },
    {
        n: 3, id: "step-available", icon: ListChecks, title: "Available Tuitions",
        summary: "Browse tuition enquiries in your city and apply to the ones you can genuinely handle.",
        body: [
            { type: "text", text: "Tutors can view all available tuition enquiries in their city. Apply only when every one of the four criteria below matches your profile:" },
            { type: "grid", items: [
                { icon: GraduationCap, title: "Grade match", desc: "The class/level matches your expertise." },
                { icon: BookOpen, title: "Subject match", desc: "The subject is one you actively teach." },
                { icon: MapPin, title: "Location suitable", desc: "The address is inside your travel radius." },
                { icon: Clock, title: "You are available", desc: "The proposed schedule fits your calendar." },
            ]},
            { type: "tip", tone: "blue", text: "Read all terms and guidelines before applying. Enquiries from different areas of your city may be visible — apply only to assignments you can genuinely handle." },
        ],
    },
    {
        n: 4, id: "step-selection", icon: Users2, title: "Tutor Selection Process",
        summary: "Many tutors may apply. The admin team picks one based on fit, quality and availability.",
        body: [
            { type: "text", text: "Multiple tutors may apply for the same enquiry. The Anand Tutorials admin team selects one tutor based on the following weighted criteria:" },
            { type: "list", items: [
                "Teaching Area (proximity to student's home)",
                "Qualification (degree, specialisation, certifications)",
                "Experience (years, results, subject-specific)",
                "Parent Feedback from previous assignments",
                "Profile Quality (completeness, photo, videos, bio)",
                "Availability (schedule overlap with parent's request)",
            ]},
            { type: "callout", tone: "slate", text: "If an enquiry disappears from Available Tuitions, it means it has already been assigned to another tutor. Don't panic — new leads are posted daily." },
        ],
    },
    {
        n: 5, id: "step-daily-leads", icon: CalendarPlus, title: "Daily New Tuition Leads",
        summary: "Fresh enquiries are posted every day — habit matters.",
        body: [
            { type: "text", text: "New tuition enquiries are posted regularly throughout the day. Tutors who check Available Tuitions in the morning and evening consistently get the best assignments." },
            { type: "tip", tone: "green", text: "Turn on push notifications for the Book My Tutor app so you're the first to see new leads in your area." },
        ],
    },
    {
        n: 6, id: "step-notification", icon: Bell, title: "Assignment Notification",
        summary: "When you're picked, you'll be notified inside the app and by phone.",
        body: [
            { type: "list", items: [
                "You will receive an in-app notification with the student's details.",
                "The admin team may also call you to confirm your availability.",
                "Respond quickly — delayed responses may cause the assignment to be re-allocated.",
            ]},
        ],
    },
    {
        n: 7, id: "step-my-demo", icon: Presentation, title: "My Demo",
        summary: "The assigned enquiry moves to \"My Demo\" with full student details.",
        body: [
            { type: "text", text: "Once selected, the enquiry appears in the My Demo tab of the app. You will receive complete student details — name, class, subjects, parent contact, and the reference institute." },
            { type: "callout", tone: "blue", text: "Book My Tutor is a tutoring platform used by multiple institutes. Every enquiry carries an institute reference — always introduce yourself using the assigned institute name only." },
            { type: "list", title: "Institutes on Book My Tutor", items: [
                "Anand Tutorials", "Unique Home Tutors", "Chanakya Tutorials", "Tutor Me", "Other partner educational organizations",
            ]},
        ],
    },
    {
        n: 8, id: "step-demo", icon: GraduationCap, title: "Conduct Free Demo Class",
        summary: "Schedule one free demo — be punctual, teach professionally, coordinate with parents.",
        body: [
            { type: "list", title: "Demo checklist", items: [
                "Confirm the exact date, time and address with the parent.",
                "Arrive 5-10 minutes early — respect creates trust.",
                "Introduce yourself using the assigned institute name.",
                "Bring a structured 45-60 min lesson plan (concept + example + practice).",
                "End with a short summary and clear next-class plan.",
            ]},
            { type: "warning", tone: "amber", title: "If parents ask about fees",
              text: "Kindly contact the institute regarding fees. Tutors are not authorized to discuss or negotiate fees with parents." },
        ],
    },
    {
        n: 9, id: "step-no-direct", icon: ShieldAlert, title: "Strict No Direct Dealing Policy",
        summary: "Zero tolerance. Bypassing the institute is grounds for permanent removal.",
        body: [
            { type: "warning", tone: "red", title: "Tutors must never",
              items: [
                "Accept direct payments from parents",
                "Negotiate fees with parents",
                "Continue the tuition privately outside the institute",
                "Bypass the institute in any form",
                "Share personal UPI / bank / payment details with parents",
              ],
              footer: "Violation results in: permanent account blocking · immediate removal from Book My Tutor · no second chance · permanent loss of future opportunities across all partner institutes." },
        ],
    },
    {
        n: 10, id: "step-demo-completion", icon: CheckCircle2, title: "Demo Completion",
        summary: "Inform the institute and mark the demo as completed in the app.",
        body: [
            { type: "list", items: [
                "Immediately after the demo, phone or WhatsApp the institute with a brief update.",
                "Open the Book My Tutor app → My Demo → mark the demo as \"Completed\".",
                "Wait for the institute to collect parent feedback (usually within 24 hours).",
            ]},
        ],
    },
    {
        n: 11, id: "step-wait-confirm", icon: Clock, title: "Wait for Institute Confirmation",
        summary: "Regular classes start ONLY after official institute confirmation.",
        body: [
            { type: "text", text: "The institute will collect parent feedback and process the confirmation. Only after receiving official confirmation from the institute may tutors begin regular classes." },
            { type: "warning", tone: "amber", title: "If parents request classes before confirmation, politely reply",
              text: "Kindly connect with the institute first. I will start regular classes only after receiving official confirmation." },
        ],
    },
    {
        n: 12, id: "step-tuition-starts", icon: BookOpen, title: "Tuition Starts",
        summary: "The assignment moves to \"My Tuitions\" and regular classes begin.",
        body: [
            { type: "text", text: "Once approved, the tuition will appear under My Tuitions in the app. From here you can view the class schedule, mark attendance, and access student notes." },
        ],
    },
    {
        n: 13, id: "step-attendance", icon: ClipboardCheck, title: "Mark Attendance",
        summary: "Mark attendance after EVERY class — no exceptions.",
        body: [
            { type: "text", text: "Marking attendance after every class is mandatory. It powers your payment calculation, parent records, and the institute's monthly reporting." },
            { type: "tip", tone: "green", text: "Attendance is mandatory for accurate records and smooth payment processing. Miss a mark → risk a payment delay." },
        ],
    },
    {
        n: 14, id: "step-explore", icon: LayoutDashboard, title: "Explore the App",
        summary: "Book My Tutor packs everything a tutor needs — nine core modules to master.",
        body: [
            { type: "feature-grid", items: [
                { icon: Presentation, title: "My Demo", desc: "Assigned enquiries awaiting your demo class." },
                { icon: CheckCircle2, title: "Completed Demo", desc: "History of demos + parent feedback status." },
                { icon: BookOpen, title: "My Tuitions", desc: "Active regular tuitions with schedules." },
                { icon: ClipboardCheck, title: "Attendance", desc: "Class-by-class attendance & records." },
                { icon: Star, title: "Study X", desc: "Curated study material and lesson templates." },
                { icon: Clock, title: "Timetable", desc: "Your weekly schedule at a glance." },
                { icon: CalendarDays, title: "Calendar", desc: "Month view — plan and reschedule easily." },
                { icon: Share2, title: "Refer & Earn", desc: "Invite tutors and earn referral rewards." },
                { icon: Wallet, title: "Payments", desc: "Track upcoming and past payment cycles." },
            ]},
        ],
    },
    {
        n: 15, id: "step-support", icon: HelpCircle, title: "Need Help?",
        summary: "Anand Tutorials support is one tap away — WhatsApp, call, or email.",
        body: [
            { type: "support-block" },
        ],
    },
    {
        n: 16, id: "step-payment", icon: IndianRupee, title: "Tutor Fee & Payment Policy",
        summary: "Understand In-Hand Fee, payment schedule and fee-related support.",
        body: [
            { type: "payment-block" },
        ],
    },
];

const FAQS = [
    { q: "How do I get tuition assignments?", a: "Complete your profile fully, then check Available Tuitions inside the Book My Tutor app daily. Apply only to enquiries where grade, subject, location and availability all match. Selected tutors receive an in-app notification." },
    { q: "Why can't I see my applied enquiry anymore?", a: "If an enquiry disappears from Available Tuitions, it means the admin team has already assigned it to another tutor. Don't worry — new leads are posted every day. Keep applying to fresh ones." },
    { q: "Can multiple tutors apply for one tuition?", a: "Yes. Many tutors typically apply for the same enquiry. The admin team then evaluates each applicant on qualification, area, experience, profile quality, past parent feedback, and availability before picking one." },
    { q: "How is the final tutor selected?", a: "The admin team chooses one tutor per enquiry based on six weighted criteria: Teaching Area, Qualification, Experience, Parent Feedback, Profile Quality, and Availability." },
    { q: "When can I start regular classes?", a: "Only after receiving official confirmation from the institute. Never start regular classes on the parent's request alone — this violates the policy and can lead to permanent account blocking." },
    { q: "Can I discuss fees with parents?", a: "No. Tutors are strictly not authorized to discuss or negotiate fees with parents. All fee matters are handled exclusively by the institute. If a parent asks, politely redirect them to contact the institute." },
    { q: "What happens after the demo class?", a: "Immediately inform the institute, mark the demo as completed in the app, and wait for the institute to collect parent feedback and issue official confirmation. Only after confirmation should you begin regular classes." },
    { q: "Why is attendance important?", a: "Attendance is the single source of truth for payment calculation, parent records, and institute reporting. Marking attendance after every class ensures accurate records and smooth, on-time payment processing." },
    { q: "What is the In Hand Fee?", a: "The In Hand Fee shown in the Book My Tutor app is your final payable amount after the institute's profit-sharing/service charges have already been deducted. You will receive exactly this amount after completing all scheduled classes with attendance." },
    { q: "What if the In Hand Fee is not shown?", a: "Some enquiries do not display an In Hand Fee — this means the tuition fee has not yet been finalized. The institute will confirm your payment only after a successful demo class and discussion with the parent." },
    { q: "When are tutor payments released?", a: "Tutor payments are processed at the end of every month after all scheduled classes are completed and attendance is verified by the institute." },
    { q: "What happens if I deal directly with parents?", a: "Direct dealing (accepting payments, negotiating fees, continuing tuition privately, or bypassing the institute) results in permanent account blocking, immediate removal from Book My Tutor, and a permanent loss of future opportunities across all partner institutes. No second chance." },
    { q: "How often are new tuition enquiries posted?", a: "New tuition enquiries are posted daily throughout the day. Enable push notifications and check the Available Tuitions tab in the morning and evening for the best pick." },
];

/* -----------------------------------------------------------------------------
 *  Small building-block components
 * ---------------------------------------------------------------------------*/

function InfoTip({ tone = "blue", children }) {
    const map = {
        blue:  "border-blue-300 bg-blue-50 text-blue-900",
        amber: "border-amber-300 bg-amber-50 text-amber-900",
        green: "border-emerald-300 bg-emerald-50 text-emerald-900",
        slate: "border-slate-300 bg-slate-50 text-slate-800",
    };
    return (
        <div className={`rounded-xl border-l-4 ${map[tone]} p-4 text-sm leading-relaxed`}>
            {children}
        </div>
    );
}

function WarningBlock({ tone = "red", title, text, items, footer }) {
    const isRed = tone === "red";
    const wrap = isRed
        ? "border-red-300 bg-red-50 text-red-900"
        : "border-amber-300 bg-amber-50 text-amber-900";
    const iconClass = isRed ? "text-red-600" : "text-amber-600";
    return (
        <div className={`rounded-2xl border-2 ${wrap} p-5`}>
            <div className="flex items-start gap-3">
                <ShieldAlert className={`w-6 h-6 shrink-0 mt-0.5 ${iconClass}`} />
                <div className="flex-1 min-w-0">
                    {title && <div className="font-heading font-bold text-base mb-2">{title}</div>}
                    {text && <p className="text-sm leading-relaxed mb-2">{text}</p>}
                    {items && (
                        <ul className="text-sm space-y-1.5 list-disc pl-5">
                            {items.map((it) => <li key={it}>{it}</li>)}
                        </ul>
                    )}
                    {footer && <div className="mt-3 pt-3 border-t border-current/20 text-xs font-semibold">{footer}</div>}
                </div>
            </div>
        </div>
    );
}

function StepBody({ blocks }) {
    return (
        <div className="space-y-4">
            {blocks.map((b, i) => {
                if (b.type === "text") return <p key={i} className="text-slate-700 leading-relaxed">{b.text}</p>;
                if (b.type === "list") return (
                    <div key={i}>
                        {b.title && <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{b.title}</div>}
                        <ul className="space-y-2">
                            {b.items.map((it) => (
                                <li key={it} className="flex items-start gap-2 text-slate-700">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                                    <span className="text-sm">{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
                if (b.type === "tip") return <InfoTip key={i} tone={b.tone}><strong>Tip · </strong>{b.text}</InfoTip>;
                if (b.type === "callout") return <InfoTip key={i} tone={b.tone || "blue"}>{b.text}</InfoTip>;
                if (b.type === "warning") return <WarningBlock key={i} {...b} />;
                if (b.type === "grid") return (
                    <div key={i} className="grid sm:grid-cols-2 gap-3">
                        {b.items.map((it) => (
                            <div key={it.title} className="rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-md transition-all">
                                <it.icon className="w-5 h-5 text-blue-600 mb-2" />
                                <div className="font-semibold text-slate-900 text-sm mb-1">{it.title}</div>
                                <div className="text-xs text-slate-600">{it.desc}</div>
                            </div>
                        ))}
                    </div>
                );
                if (b.type === "feature-grid") return (
                    <div key={i} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {b.items.map((it) => (
                            <div key={it.title} className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:border-blue-300 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center mb-2.5">
                                    <it.icon className="w-4.5 h-4.5 text-blue-700" />
                                </div>
                                <div className="font-semibold text-slate-900 text-sm mb-1">{it.title}</div>
                                <div className="text-xs text-slate-600 leading-relaxed">{it.desc}</div>
                            </div>
                        ))}
                    </div>
                );
                if (b.type === "phone-mockup") return <PhoneMockup key={i} caption={b.caption} />;
                if (b.type === "support-block") return <SupportBlock key={i} />;
                if (b.type === "payment-block") return <PaymentBlock key={i} />;
                return null;
            })}
        </div>
    );
}

/* Simple CSS-drawn phone mockup showing the Book My Tutor home screen. No
 * external asset — keeps the page lightweight and SEO-safe. */
function PhoneMockup({ caption }) {
    return (
        <div className="flex flex-col items-center py-4">
            <div className="relative w-[260px] rounded-[2.2rem] border-[10px] border-slate-900 bg-slate-900 shadow-2xl">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-slate-900 rounded-b-xl z-10" />
                <div className="rounded-[1.5rem] overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-slate-900 text-white h-[480px] flex flex-col">
                    <div className="pt-6 pb-2 px-4 text-center">
                        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Book My Tutor</div>
                        <div className="text-lg font-heading font-bold mt-1">Home</div>
                    </div>
                    <div className="px-3 grid grid-cols-2 gap-2 mt-2">
                        {[
                            { label: "My Demo", icon: Presentation },
                            { label: "Completed", icon: CheckCircle2 },
                            { label: "My Tuitions", icon: BookOpen },
                            { label: "Attendance", icon: ClipboardCheck },
                            { label: "Study X", icon: Star },
                            { label: "Timetable", icon: Clock },
                            { label: "Calendar", icon: CalendarDays },
                            { label: "Refer & Earn", icon: Share2 },
                        ].map((t) => (
                            <div key={t.label} className="rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm p-2.5 flex flex-col items-center gap-1">
                                <t.icon className="w-4 h-4 text-amber-300" />
                                <span className="text-[10px] font-semibold">{t.label}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-auto px-3 pb-5">
                        <div className="rounded-xl bg-amber-400 text-slate-900 py-2.5 text-center text-xs font-bold shadow-lg">Available Tuitions →</div>
                    </div>
                </div>
            </div>
            {caption && <div className="text-xs text-slate-500 mt-3">{caption}</div>}
        </div>
    );
}

function SupportBlock() {
    return (
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-blue-50 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-1">Anand Tutorials Support</div>
            <div className="font-heading font-bold text-lg text-slate-900 mb-4">We reply within minutes.</div>
            <div className="space-y-2 mb-4">
                {SUPPORT_PHONES.map((p) => (
                    <a key={p} href={`tel:${p.replace(/\s+/g, "")}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-700 transition-colors" data-testid={`tg-support-phone-${p.replace(/\D/g,'')}`}>
                        <PhoneCall className="w-4 h-4 text-blue-600" /> <span className="font-mono font-semibold">{p}</span>
                    </a>
                ))}
            </div>
            <div className="flex flex-wrap gap-2">
                <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${CONTACT_MESSAGE}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-bold shadow" data-testid="tg-support-whatsapp">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <a href={`tel:${SUPPORT_PHONES[0].replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold shadow" data-testid="tg-support-call">
                    <PhoneCall className="w-4 h-4" /> Call
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-slate-800 text-sm font-semibold" data-testid="tg-support-contact">
                    Contact form <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        </div>
    );
}

function PaymentBlock() {
    return (
        <div className="space-y-4">
            {/* In-hand fee (mentioned) */}
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-5 h-5 text-emerald-700" />
                    <div className="font-heading font-bold text-emerald-900">Tutor In-Hand Fee</div>
                </div>
                <ul className="space-y-2 text-sm text-emerald-900">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>If an <strong>In Hand Fee</strong> is displayed in the app, it is your <strong>final payable amount</strong>.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>The institute&apos;s profit-sharing / service charges have <strong>already been deducted</strong>.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>You will receive exactly this amount after completing all scheduled classes with attendance.</span></li>
                </ul>
                <div className="mt-4 rounded-lg border border-emerald-300 bg-white/70 p-3 text-xs text-emerald-900 italic">
                    The In Hand Fee shown in the app is the tutor&apos;s final payable amount after the institute&apos;s profit-sharing ratio has already been deducted.
                </div>
            </div>

            {/* When In-Hand fee is NOT displayed */}
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5">
                <div className="flex items-center gap-2 mb-2">
                    <HelpCircle className="w-5 h-5 text-amber-700" />
                    <div className="font-heading font-bold text-amber-900">If In-Hand Fee Is Not Mentioned</div>
                </div>
                <p className="text-sm text-amber-900 leading-relaxed">
                    Some enquiries do not display an In Hand Fee — this means the tuition fee has <strong>not yet been finalized</strong>. The institute will decide and confirm the tutor&apos;s payment only after the successful demo class and discussion with the parent.
                </p>
                <div className="mt-3 rounded-lg border border-amber-400 bg-white/70 p-3 text-xs text-amber-900 italic">
                    If no In Hand Fee is displayed, the tutor&apos;s payment has not yet been finalized. It will be confirmed only after the demo class.
                </div>
            </div>

            {/* Payment Schedule */}
            <div className="rounded-2xl border border-emerald-300 bg-white p-5">
                <div className="flex items-center gap-2 mb-2">
                    <CalendarDays className="w-5 h-5 text-emerald-700" />
                    <div className="font-heading font-bold text-slate-900">Payment Schedule</div>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>Payments are processed at the <strong>end of every month</strong>.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>Attendance must be marked regularly and completely.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>Payments release after verification of completed classes and attendance.</span></li>
                </ul>
                <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900 italic">
                    Payments are processed at the end of every month after successful completion of scheduled classes and attendance verification.
                </div>
            </div>

            {/* Fee-related support */}
            <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5">
                <div className="flex items-center gap-2 mb-2">
                    <PhoneCall className="w-5 h-5 text-blue-700" />
                    <div className="font-heading font-bold text-slate-900">Fee-Related Support</div>
                </div>
                <p className="text-sm text-slate-700 mb-3">For all fee, payment, and salary-related queries, contact:</p>
                <div className="font-mono font-bold text-blue-800 mb-4">📞 Call Tutor Support</div>
                <div className="flex flex-wrap gap-2 mb-4">
                    <a href="tel:+917000530316" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold shadow" data-testid="tg-payment-call">
                        <PhoneCall className="w-4 h-4" /> Call Now
                    </a>
                    <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${CONTACT_MESSAGE}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-bold shadow" data-testid="tg-payment-whatsapp">
                        <MessageCircle className="w-4 h-4" /> WhatsApp Support
                    </a>
                </div>
                <div className="rounded-lg border-l-4 border-red-400 bg-red-50 p-3 text-xs text-red-900">
                    <strong>Important —</strong> Tutors must <strong>never</strong> discuss or negotiate fees directly with parents. All fee-related matters are handled exclusively by the institute.
                </div>
            </div>
        </div>
    );
}

/* -----------------------------------------------------------------------------
 *  Page
 * ---------------------------------------------------------------------------*/

export default function TutorGuide() {
    const [activeStep, setActiveStep] = useState("step-profile");
    const [query, setQuery] = useState("");
    const [openFaq, setOpenFaq] = useState(null);
    const [copyOk, setCopyOk] = useState(false);

    // Track scroll to highlight the current step in the sticky nav
    useEffect(() => {
        const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((e) => { if (e.isIntersecting) setActiveStep(e.target.id); });
        }, opts);
        STEPS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const filteredSteps = useMemo(() => {
        if (!query.trim()) return STEPS;
        const q = query.toLowerCase();
        return STEPS.filter((s) =>
            s.title.toLowerCase().includes(q) ||
            s.summary.toLowerCase().includes(q) ||
            JSON.stringify(s.body).toLowerCase().includes(q)
        );
    }, [query]);

    const filteredFaqs = useMemo(() => {
        if (!query.trim()) return FAQS;
        const q = query.toLowerCase();
        return FAQS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
    }, [query]);

    // Reading time estimate (rough — ~200 wpm)
    const readingMinutes = 12;

    const share = async () => {
        const url = "https://anandtutorials.com/tutor-guide";
        if (navigator.share) {
            try {
                await navigator.share({ title: "Book My Tutor · Tutor Guide", url });
            } catch { /* dismissed */ }
        } else {
            await copy();
        }
    };
    const copy = async () => {
        try {
            await navigator.clipboard.writeText("https://anandtutorials.com/tutor-guide");
            setCopyOk(true);
            setTimeout(() => setCopyOk(false), 1500);
        } catch { /* clipboard unsupported */ }
    };
    const printGuide = () => window.print();

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map((f) => ({
            "@type": "Question", "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
    };
    const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "Book My Tutor — Tutor Onboarding Guide",
        "description": "Complete step-by-step process for tutors on Book My Tutor: registration, applying to tuition enquiries, demo class, attendance, payment.",
        "step": STEPS.map((s) => ({
            "@type": "HowToStep",
            "position": s.n,
            "name": s.title,
            "text": s.summary,
            "url": `https://anandtutorials.com/tutor-guide#${s.id}`,
        })),
    };
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Become a Tutor", "item": "https://anandtutorials.com/become-a-tutor" },
            { "@type": "ListItem", "position": 3, "name": "Tutor Guide", "item": "https://anandtutorials.com/tutor-guide" },
        ],
    };

    return (
        <div className="bg-gradient-to-b from-slate-50 via-white to-slate-50 min-h-screen" data-testid="tutor-guide-page">
            <Helmet>
                <title>Tutor Guide · Book My Tutor — Complete Tutor Onboarding Process | Anand Tutorials</title>
                <meta name="description" content="Complete Book My Tutor · Tutor Guide: registration, profile completion, applying to tuition enquiries, demo class, institute confirmation, attendance, payments & In Hand Fee policy. Read the official Anand Tutorials tutor onboarding guide." />
                <meta name="keywords" content="Book My Tutor Guide, Tutor Guide, Home Tutor Guide, Tutor Registration Process, Tutor Onboarding, Book My Tutor Tutorial, Tuition Assignment Process, Home Tuition Tutor Process, Tutor Demo Process, Tutor Payment Policy, Tutor In Hand Fee, Tutor Attendance Guide, Home Tuition Jobs, Private Tutor Guide, Anand Tutorials Tutor Guide" />
                <link rel="canonical" href="https://anandtutorials.com/tutor-guide" />
                <meta property="og:title" content="Book My Tutor · Tutor Guide — Complete Onboarding Process" />
                <meta property="og:description" content="Step-by-step guide for tutors on Book My Tutor: registration, applying to tuitions, demo, attendance, In Hand Fee & payment policy." />
                <meta property="og:type" content="article" />
                <meta property="og:url" content="https://anandtutorials.com/tutor-guide" />
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(buildConversationalFAQSchema(TUTOR_GUIDE_AEO.faqs))}</script>
            </Helmet>

            {/* ══════ Hero ══════ */}
            <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 10%, #fbbf24 0%, transparent 40%), radial-gradient(circle at 85% 90%, #3B82F6 0%, transparent 40%)" }} />
                <div className="relative max-w-5xl mx-auto px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24">
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-1.5 text-xs text-blue-200 mb-6" aria-label="Breadcrumb" data-testid="tg-breadcrumb">
                        <Link to="/" className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
                        <ChevronRight className="w-3 h-3" />
                        <Link to="/become-a-tutor" className="hover:text-white">Become a Tutor</Link>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-amber-300">Tutor Guide</span>
                    </nav>

                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/25 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 mb-5">
                        <Sparkles className="w-3 h-3" /> Official Tutor Documentation
                    </div>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight max-w-3xl" data-testid="tg-h1">
                        Book My Tutor — <span className="text-amber-300">Tutor Guide</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-blue-100/85 leading-relaxed max-w-2xl">
                        Learn the complete process from registration to receiving tuition assignments, conducting demo classes, starting regular classes, attendance, and payment.
                    </p>

                    {/* Reading time + share tools */}
                    <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-blue-100/70">
                        <span className="inline-flex items-center gap-1"><Timer className="w-3.5 h-3.5" /> {readingMinutes} min read</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> 16 steps · {FAQS.length} FAQs</span>
                        <span>·</span>
                        <button onClick={share} className="inline-flex items-center gap-1 hover:text-amber-300" data-testid="tg-share"><Share2 className="w-3.5 h-3.5" /> Share</button>
                        <button onClick={copy} className="inline-flex items-center gap-1 hover:text-amber-300" data-testid="tg-copy-link">
                            <Copy className="w-3.5 h-3.5" /> {copyOk ? "Copied ✓" : "Copy link"}
                        </button>
                        <button onClick={printGuide} className="inline-flex items-center gap-1 hover:text-amber-300" data-testid="tg-print"><Printer className="w-3.5 h-3.5" /> Print</button>
                        <a href="/tutor-guide" onClick={(e) => { e.preventDefault(); printGuide(); }} className="inline-flex items-center gap-1 hover:text-amber-300" data-testid="tg-download">
                            <Download className="w-3.5 h-3.5" /> Save as PDF
                        </a>
                    </div>

                    {/* Primary CTAs */}
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <a href="#step-profile" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-amber-400 text-slate-900 font-bold text-sm hover:bg-amber-300 shadow-xl hover:-translate-y-0.5 transition-all" data-testid="tg-cta-read">
                            <BookOpen className="w-4 h-4" /> Read Tutor Guide <ArrowRight className="w-4 h-4" />
                        </a>
                        <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-white/20 hover:border-amber-400/60 hover:bg-white/5 text-white font-semibold text-sm transition-all" data-testid="tg-cta-support">
                            <PhoneCall className="w-4 h-4" /> Contact Support
                        </Link>
                        <a href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold text-sm transition-all" data-testid="tg-cta-app">
                            <ExternalLink className="w-4 h-4" /> Open Book My Tutor App
                        </a>
                    </div>
                </div>
            </section>

            {/* V65 — AEO Quick-Answer card */}
            <QuickAnswerCard
                question={TUTOR_GUIDE_AEO.quickAnswer.question}
                answer={TUTOR_GUIDE_AEO.quickAnswer.answer}
                bullets={TUTOR_GUIDE_AEO.quickAnswer.bullets}
                testId="tutor-guide-quick-answer"
            />

            {/* ══════ Search bar + sticky ══════ */}
            <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 print:hidden" data-testid="tg-search-bar">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3">
                    <Search className="w-4 h-4 text-slate-500 shrink-0" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search steps, policies, payments, FAQs…"
                        aria-label="Search the Tutor Guide"
                        className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                        data-testid="tg-search-input"
                    />
                    {query && (
                        <button onClick={() => setQuery("")} className="text-xs text-slate-500 hover:text-slate-800" data-testid="tg-search-clear">Clear</button>
                    )}
                </div>
            </div>

            {/* ══════ Body : sidebar nav + timeline ══════ */}
            <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 grid lg:grid-cols-[240px_1fr] gap-10">

                {/* Sidebar progress nav (desktop) */}
                <aside className="hidden lg:block sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto print:hidden" aria-label="On this page" data-testid="tg-side-nav">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">On this page</div>
                    <ol className="space-y-1">
                        {STEPS.map((s) => {
                            const active = activeStep === s.id;
                            return (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${active ? "bg-blue-600 text-white font-semibold" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}
                                        data-testid={`tg-side-nav-${s.id}`}
                                    >
                                        <span className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>{s.n}</span>
                                        <span className="leading-tight">{s.title}</span>
                                    </a>
                                </li>
                            );
                        })}
                        <li className="pt-2 mt-2 border-t border-slate-200">
                            <a href="#faqs" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100" data-testid="tg-side-nav-faqs">
                                <HelpCircle className="w-3.5 h-3.5" /> FAQs
                            </a>
                        </li>
                    </ol>
                </aside>

                {/* Timeline */}
                <main className="min-w-0">
                    {filteredSteps.length === 0 && (
                        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center text-slate-500">
                            No steps match &quot;{query}&quot;. <button className="text-blue-700 underline ml-1" onClick={() => setQuery("")}>Clear search</button>
                        </div>
                    )}

                    <ol className="relative border-l-2 border-slate-200 space-y-10 ml-4" data-testid="tg-timeline">
                        {filteredSteps.map((s) => {
                            const Icon = s.icon;
                            return (
                                <li key={s.id} id={s.id} className="scroll-mt-24 relative pl-8" data-testid={`tg-${s.id}`}>
                                    {/* Step icon marker */}
                                    <div className="absolute -left-[26px] top-0 w-12 h-12 rounded-full bg-white border-4 border-blue-600 shadow-lg flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-blue-700" />
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 sm:p-7">
                                        <div className="flex flex-wrap items-center gap-2 mb-3">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 text-[10px] font-bold uppercase tracking-widest">Step {s.n}</span>
                                        </div>
                                        <h2 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 mb-2">{s.title}</h2>
                                        <p className="text-sm text-slate-600 mb-5">{s.summary}</p>
                                        <StepBody blocks={s.body} />
                                    </div>
                                </li>
                            );
                        })}
                    </ol>

                    {/* Internal-link block */}
                    <div className="mt-14 grid sm:grid-cols-2 gap-3">
                        <Link to="/become-a-tutor" className="rounded-2xl border border-slate-200 hover:border-blue-400 hover:shadow-lg bg-gradient-to-br from-white to-blue-50 p-5 group transition-all" data-testid="tg-link-become">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-1">Not registered yet?</div>
                            <div className="font-heading font-bold text-slate-900 text-base mb-1 group-hover:text-blue-700">Apply as a Home / Online Tutor</div>
                            <div className="text-xs text-slate-600">Fill the 5-minute application to start receiving tuition assignments across India & worldwide.</div>
                        </Link>
                        <Link to="/contact" className="rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-lg bg-gradient-to-br from-white to-emerald-50 p-5 group transition-all" data-testid="tg-link-contact">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Still stuck?</div>
                            <div className="font-heading font-bold text-slate-900 text-base mb-1 group-hover:text-emerald-700">Contact Anand Tutorials</div>
                            <div className="text-xs text-slate-600">Phone, WhatsApp, email or contact form — our support team replies within minutes.</div>
                        </Link>
                    </div>

                    {/* ══════ FAQs ══════ */}
                    <section id="faqs" className="mt-16 scroll-mt-24" data-testid="tg-faqs">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-2">Frequently Asked</div>
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 mb-6">Tutor Guide FAQs</h2>
                        <div className="space-y-2">
                            {filteredFaqs.map((f, i) => {
                                const idx = FAQS.indexOf(f);
                                const isOpen = openFaq === idx;
                                return (
                                    <div key={f.q} className={`rounded-xl border transition-colors ${isOpen ? "border-blue-300 bg-blue-50/50" : "border-slate-200 bg-white"}`} data-testid={`tg-faq-${i}`}>
                                        <button
                                            onClick={() => setOpenFaq(isOpen ? null : idx)}
                                            className="w-full flex items-start justify-between gap-3 text-left px-4 py-3.5"
                                            aria-expanded={isOpen}
                                        >
                                            <span className="font-semibold text-slate-900 text-sm">{f.q}</span>
                                            <ChevronDown className={`w-4 h-4 mt-1 shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                        </button>
                                        {isOpen && (
                                            <div className="px-4 pb-4 text-sm text-slate-700 leading-relaxed border-t border-blue-200/60">
                                                <p className="pt-3">{f.a}</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {filteredFaqs.length === 0 && (
                                <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                                    No FAQ matches &quot;{query}&quot;.
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Final CTA */}
                    <section className="mt-16 rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white p-8 sm:p-12 text-center shadow-2xl" data-testid="tg-final-cta">
                        <PenLine className="w-8 h-8 text-amber-300 mx-auto mb-3" />
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Ready to receive your first tuition?</h2>
                        <p className="text-blue-100/85 max-w-xl mx-auto text-sm sm:text-base mb-6">Open the Book My Tutor app, complete your profile, and start applying to tuition enquiries in your city today.</p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <a href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" data-testid="tg-download-googleplay" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-slate-900 font-bold hover:bg-amber-300 shadow-xl">
                                <ExternalLink className="w-4 h-4" /> Google Play
                            </a>
                            <a href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" data-testid="tg-download-appstore" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 font-semibold">
                                <ExternalLink className="w-4 h-4" /> App Store
                            </a>
                            <Link to="/become-a-tutor" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-amber-400/50 hover:border-amber-400 font-semibold">
                                Apply as Tutor <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </section>
                </main>
            </div>

            {/* V65 — AEO Conversational FAQ (long-form, voice-search + LLM ingestion) */}
            <ConversationalFAQ
                heading="Deeper questions about being a tutor on the panel"
                subheading="Long-form answers to the questions we most often hear from tutors during and after onboarding."
                items={TUTOR_GUIDE_AEO.faqs}
                testId="tutor-guide-conversational-faq"
            />

            {/* Print-only footer for the PDF export */}
            <div className="hidden print:block px-6 py-4 text-xs text-slate-600 border-t border-slate-200 mt-8">
                Anand Tutorials · Book My Tutor · Tutor Guide · https://anandtutorials.com/tutor-guide
            </div>
        </div>
    );
}
